import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { getProductSellHistorySummary, getProductSellList } from '../../api/product'
import type { GetProductSellHistoryListParams } from '../../api/product'

import { Content } from '../../components/container/Content'
import { CenterWrap, CenterItem, RowBetween, ColumnItem } from '../../components/common'
import { Text } from '../../components/text'
import { Sort } from '../../components/paramOption/Sort'
import { InfinityScroll } from '../../components/pagingList/InfinityScroll'
import { Spinner } from '../../components/spinner/Spinner'
import { EmptyPage } from '../../components/exception/EmptyPage'

import { Color } from '../../theme/color'

import { ReactComponent as GraphIcon } from '../../assets/gui/rise_40.svg'
import { ReactComponent as ClockIcon } from '../../assets/gui/clock_40.svg'
import { ReactComponent as InterestIcon } from '../../assets/ico/market/interest.svg'
import { ReactComponent as TimerIcon } from '../../assets/ico/market/timer.svg'

const SORT_LIST: { type: GetProductSellHistoryListParams['sort']; title: string }[] = [
  { type: 'sellDate,desc', title: '최근순' },
  { type: 'sellDate,asc', title: '과거순' },
]

export function ProductSellHistory() {
  const [sort, setSort] = useState<GetProductSellHistoryListParams['sort']>('sellDate,desc')

  const summary = useQuery({
    queryKey: [QUERY_KEY.productSellHistorySummary],
    queryFn: getProductSellHistorySummary,
  })

  const { data, isPending, isError, hasNextPage, refetch, fetchNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEY.productSellHistory, { sort }],
    queryFn: async ({ pageParam = 0 }) => await getProductSellList({ offset: pageParam, size: 10, sort }),
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.offset + lastPage.size),
    initialPageParam: 0,
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.list).flat(),
    }),
  })

  if (summary.isPending || summary.isError || isPending || isError) {
    return (
      <Content>
        <CenterWrap>
          <Spinner />
        </CenterWrap>
      </Content>
    )
  }

  if (!summary || !data.pages.length) {
    return <EmptyPage content="조회 가능한 내역이 없습니다." />
  }

  return (
    <Content id="infinity-scroll-target" isPullEvent={true} onPull={refetch}>
      <InfinityScroll length={data.pages.length} isLast={!hasNextPage} next={fetchNextPage}>
        <InfoContainer>
          <InfoCard $gap={8}>
            <ClockIcon />
            <ColumnItem $gap={2}>
              <Text size={12} color="gray_6">
                평균 수익률
              </Text>
              <Text size={18} weight={600} color="primary_5">
                {summary.data.avgRevenueRate}%
              </Text>
            </ColumnItem>
          </InfoCard>
          <InfoCard $gap={8}>
            <GraphIcon />
            <ColumnItem $gap={2}>
              <Text size={12} color="gray_6">
                평균 보유기간
              </Text>
              <Text size={18} weight={600} color="primary_5">
                {summary.data.avgHoldingDays}일
              </Text>
            </ColumnItem>
          </InfoCard>
        </InfoContainer>
        <ColumnItem>
          <SortContainer>
            <Sort<GetProductSellHistoryListParams['sort']> sortType={sort} sortList={SORT_LIST} setSortType={setSort} />
          </SortContainer>
          {data?.pages.map(({ productId, product, simplifiedRevenueRate, annualizedRevenueRate, holdingDays }) => (
            <SellProductList key={productId} $gap={8}>
              <SellProductItem $gap={8}>
                <Text size={14} color="text_secondary">
                  {product?.artist}
                </Text>
                <Text size={18} weight={600}>
                  {product?.name}
                </Text>
                <SellProductInfo>
                  <CenterItem $gap={24}>
                    <SellProductInfoIconBox $gap={4}>
                      <InterestIcon />
                      <Text size={14} color="text_secondary">
                        수익률
                      </Text>
                    </SellProductInfoIconBox>
                    <ColumnItem $gap={4}>
                      <Text size={18} weight={600} color={'error'}>
                        {/* TODO: 음수면 style 변경해야됨 */}
                        {simplifiedRevenueRate}%
                      </Text>
                      <Text size={10} color="text_secondary" $textAlign="center">
                        (연 {annualizedRevenueRate}%)
                      </Text>
                    </ColumnItem>
                  </CenterItem>
                  <CenterItem $gap={24}>
                    <SellProductInfoIconBox $gap={4}>
                      <TimerIcon />
                      <Text size={14} color="text_secondary">
                        보유기간
                      </Text>
                    </SellProductInfoIconBox>
                    <Text size={18} weight={600}>
                      {holdingDays}일
                    </Text>
                  </CenterItem>
                </SellProductInfo>
              </SellProductItem>
            </SellProductList>
          ))}
        </ColumnItem>
      </InfinityScroll>
    </Content>
  )
}

const SortContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px;
`

const InfoContainer = styled.div`
  display: flex;
  gap: 16px;
  padding: 24px 16px;
  background-color: ${Color.gray_1};
`

const InfoCard = styled(CenterItem)`
  width: 100%;
  padding: 16px 0;
  border-radius: 8px;
  background-color: ${Color.white};
  box-shadow: 0 4px 6px 0 rgba(0, 0, 0, 0.05);
`

const SellProductList = styled(ColumnItem)`
  background-color: ${Color.white};
`

const SellProductItem = styled(ColumnItem)`
  margin: 0 24px;
  padding: 16px 0;
`

const SellProductInfo = styled(RowBetween)`
  margin-top: 8px;
  padding: 20px 0;
  border-radius: 8px;
  background-color: ${Color.gray_1};
  > ${CenterItem} {
    width: 100%;
  }
`

const SellProductInfoIconBox = styled(ColumnItem)`
  align-items: center;
`
